// Wraps @zxing/browser's BrowserMultiFormatReader and runs a decode loop against
// a video element. Returns nothing — fires onResult via a ref so the effect
// doesn't re-mount when the consumer's callback identity changes.

import { useEffect, useRef } from 'react';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { BarcodeFormat as ZxFormat, DecodeHintType, NotFoundException } from '@zxing/library';
import type { BarcodeFormat, ScanResult } from './types';

const FORMAT_TO_ZX: Record<BarcodeFormat, ZxFormat> = {
  qr: ZxFormat.QR_CODE,
  code128: ZxFormat.CODE_128,
  code39: ZxFormat.CODE_39,
  ean13: ZxFormat.EAN_13,
  ean8: ZxFormat.EAN_8,
  upca: ZxFormat.UPC_A,
  upce: ZxFormat.UPC_E,
  itf: ZxFormat.ITF,
  codabar: ZxFormat.CODABAR,
  datamatrix: ZxFormat.DATA_MATRIX,
  pdf417: ZxFormat.PDF_417,
  aztec: ZxFormat.AZTEC,
};

const ZX_TO_FORMAT: Partial<Record<ZxFormat, BarcodeFormat>> = Object.entries(FORMAT_TO_ZX).reduce(
  (acc, [k, v]) => ({ ...acc, [v]: k as BarcodeFormat }),
  {} as Partial<Record<ZxFormat, BarcodeFormat>>,
);

interface Options {
  videoRef: React.RefObject<HTMLVideoElement>;
  deviceId: string | null;
  formats?: BarcodeFormat[];
  onResult: (result: ScanResult) => void;
  onError?: (err: Error) => void;
  active: boolean;
}

export function useBarcodeCamera({ videoRef, deviceId, formats, onResult, onError, active }: Options) {
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  // Stable key for formats array (caller doesn't have to memo)
  const formatsKey = formats?.slice().sort().join(',') ?? '';

  useEffect(() => {
    if (!active || !deviceId || !videoRef.current) return;

    const hints = new Map();
    if (formats && formats.length) {
      hints.set(
        DecodeHintType.POSSIBLE_FORMATS,
        formats.map((f) => FORMAT_TO_ZX[f]),
      );
    }
    const reader = new BrowserMultiFormatReader(hints);

    let cancelled = false;
    let controls: IScannerControls | null = null;

    reader
      .decodeFromVideoDevice(deviceId, videoRef.current, (result, err, ctrl) => {
        if (cancelled) return;
        if (result) {
          const fmt = ZX_TO_FORMAT[result.getBarcodeFormat() as ZxFormat] ?? 'qr';
          onResultRef.current({ text: result.getText(), format: fmt });
        } else if (err && !(err instanceof NotFoundException)) {
          // NotFoundException fires every frame with no code visible — ignore it.
          onErrorRef.current?.(err as Error);
        }
        if (!controls && ctrl) controls = ctrl;
      })
      .then((ctrl) => {
        if (cancelled) {
          ctrl.stop();
          return;
        }
        controls = ctrl;
      })
      .catch((err) => {
        if (!cancelled) onErrorRef.current?.(err as Error);
      });

    return () => {
      cancelled = true;
      controls?.stop();
      controls = null;
    };
    // formats handled via formatsKey for stability
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, deviceId, videoRef, formatsKey]);
}
